import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Twitter, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { newsletterAPI, settingsAPI } from '../../lib/api';
import { trackNewsletterSignup, trackFormSubmit } from '../../lib/analytics';
import { toast } from 'sonner';
import { getRecaptchaToken } from '../../hooks/useRecaptcha';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5b134e2c-7890-49bc-80ca-89be776cbb68/artifacts/5wn5rccf_20220722%20Reshapers%20Logo%20Icon%20110%20x%20110p.png';
const CATALYST_LOGO_URL = 'https://customer-assets.emergentagent.com/job_reshape-web/artifacts/0zymoliy_logo-small.png';

const footerLinks = {
  solutions: [
    { name: 'Transformation', path: '/solutions/transformation' },
    { name: 'Marketing & Communications', path: '/solutions/marketing-communications' },
    { name: 'Team Building', path: '/solutions/team-building' },
  ],
  company: [
    { name: 'About', path: '/about' },
    { name: 'Impact', path: '/impact' },
    { name: 'Insights', path: '/insights' },
    { name: 'Careers', path: '/careers' },
  ],
  legal: [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Cookie Policy', path: '/cookie-policy' },
    { name: 'Terms of Service', path: '/terms-of-service' },
  ],
};

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsAPI.get();
        setSettings(res.data);
      } catch {
        // silent - footer renders defaults
      }
    };
    fetchSettings();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('newsletter');
      
      await newsletterAPI.subscribe({ email, name, company, recaptcha_token: recaptchaToken });
      toast.success('Successfully subscribed to the newsletter!');
      trackNewsletterSignup();
      trackFormSubmit('newsletter');
      setEmail('');
      setName('');
      setCompany('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Build social links from settings (ensure HTTPS)
  const ensureHttps = (url) => url ? url.replace(/^http:\/\//i, 'https://') : url;
  const socialLinks = [];
  if (settings?.facebook_url) socialLinks.push({ name: 'Facebook', icon: Facebook, url: ensureHttps(settings.facebook_url) });
  if (settings?.instagram_url) socialLinks.push({ name: 'Instagram', icon: Instagram, url: ensureHttps(settings.instagram_url) });
  if (settings?.linkedin_url) socialLinks.push({ name: 'LinkedIn', icon: Linkedin, url: ensureHttps(settings.linkedin_url) });
  if (settings?.twitter_url) socialLinks.push({ name: 'Twitter', icon: Twitter, url: ensureHttps(settings.twitter_url) });
  if (settings?.youtube_url) socialLinks.push({ name: 'YouTube', icon: Youtube, url: ensureHttps(settings.youtube_url) });
  if (settings?.contact_email) socialLinks.push({ name: 'Email', icon: Mail, url: `mailto:${settings.contact_email}` });

  return (
    <footer className="bg-slate-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex items-center gap-6">
              <img 
                src={LOGO_URL} 
                alt="Reshapers" 
                className="h-24 w-24 rounded-2xl"
                loading="lazy"
              />
              <div>
                <div className="flex items-center gap-4 mb-2">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name === 'Email' ? `Email us` : `Follow us on ${social.name}`}
                      className="text-slate-400 hover:text-white transition-colors"
                      data-testid={`social-link-${social.name.toLowerCase()}`}
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
                </div>
                <h3 className="font-heading text-2xl font-bold gradient-text">
                  {settings?.newsletter_title || 'Join the list'}
                </h3>
              </div>
            </div>
            <div>
              <p className="text-slate-400 mb-4">
                {settings?.newsletter_subtitle || 'Stay in the know by signing up to our newsletter to receive tools and insights.'}
              </p>
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-full"
                    data-testid="newsletter-name-input"
                  />
                  <Input
                    type="text"
                    placeholder="Company (optional)"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-full"
                    data-testid="newsletter-company-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-full"
                    data-testid="newsletter-email-input"
                  />
                  <Button 
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:opacity-90 rounded-full px-8"
                    data-testid="newsletter-submit-btn"
                  >
                    {isSubscribing ? 'Subscribing...' : 'Sign Up'}
                  </Button>
                </div>
              </form>
              <p className="text-slate-400 text-sm mt-3">We respect your privacy.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <h4 className="font-heading font-bold text-lg mb-4">Thrive through Change</h4>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {settings?.footer_mission || 'Our mission at Reshapers is to enable people, organizations and communities to thrive through change.'}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              {settings?.contact_email && (
                <a 
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
                >
                  <Mail size={16} className="text-violet-400" />
                  {settings.contact_email}
                </a>
              )}
              {settings?.contact_phone && (
                <a 
                  href={`tel:${settings.contact_phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors text-sm"
                >
                  <Phone size={16} className="text-violet-400" />
                  {settings.contact_phone}
                </a>
              )}
              {settings?.contact_address && (
                <div className="flex items-center gap-3 text-slate-400 text-sm">
                  <MapPin size={16} className="text-violet-400" />
                  {settings.contact_address}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-slate-400 mb-2">Exclusive Dutch Caribbean Partner</p>
              <img 
                src={CATALYST_LOGO_URL} 
                alt="Catalyst Global" 
                className="h-10 brightness-0 invert opacity-70 hover:opacity-100 transition-opacity"
                loading="lazy"
              />
            </div>
          </div>

          {/* Solutions Column */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              {/* Tools link - only show if enabled */}
              {settings?.tools_library_enabled && (
                <li>
                  <Link
                    to="/tools"
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid="footer-link-tools"
                  >
                    Tools
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {settings?.site_name || 'Reshapers'}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
